import { ColumnDef } from "@tanstack/react-table";
import { Paperclip } from "@phosphor-icons/react";
import { DataTableColumnHeader } from "@repo/ui";
import type { Receipt } from "@/lib/types/inventory";
import { NdcLink } from "@/components/ndc-link";
import { DateTimeCell } from "@/components/date-time-cell";
import { NotesCell } from "@/components/notes-cell";
import { TransactionRowActions } from "@/components/transaction-row-actions";

export function createReceiptColumns(
  onView: (receipt: Receipt) => void,
  onEdit?: (receipt: Receipt) => void,
  onDelete?: (receipt: Receipt) => void,
  onEditNotes?: (receipt: Receipt) => void,
  onNDCClick?: (ndc: string) => void,
  onAddToReminder?: (receipt: Receipt) => void
): ColumnDef<Receipt>[] {
  return [
    {
      accessorKey: "date_received",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Date & Time" />
      ),
      cell: ({ row }) => {
        const dateValue = row.getValue("date_received") as Date | string;
        if (!dateValue) return <span className="text-muted-foreground">—</span>;
        const date = new Date(dateValue + "T00:00:00");
        const createdAt = row.original.created_at
          ? new Date(row.original.created_at)
          : null;
        return <DateTimeCell date={date} time={createdAt} />;
      },
      enableSorting: true,
    },
    {
      accessorKey: "item_name",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Product" />
      ),
      cell: ({ row }) => (
        <span className="font-medium">{row.getValue("item_name")}</span>
      ),
    },
    {
      accessorKey: "ndc",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="NDC" />
      ),
      cell: ({ row }) => (
        <NdcLink ndc={row.original.ndc} onClick={onNDCClick} />
      ),
    },
    {
      accessorKey: "quantity_received",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Quantity"
          className="justify-center"
        />
      ),
      cell: ({ row }) => (
        <div className="text-center font-semibold">
          {row.getValue("quantity_received")}
        </div>
      ),
    },
    {
      accessorKey: "wholesaler_name",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Wholesaler" />
      ),
      cell: ({ row }) => {
        // Handle both new format (wholesaler_name) and legacy format (wholesaler string)
        const wholesalerName = row.getValue("wholesaler_name") as
          | string
          | undefined;
        const receipt = row.original as Receipt;
        const legacyWholesaler = receipt.wholesaler;

        // If wholesaler is an object (from join), use its name
        if (
          legacyWholesaler &&
          typeof legacyWholesaler === "object" &&
          "name" in legacyWholesaler
        ) {
          return <span>{legacyWholesaler.name}</span>;
        }

        // If wholesaler_name exists, use it
        if (wholesalerName) {
          return <span>{wholesalerName}</span>;
        }

        // Fallback to legacy string field
        if (typeof legacyWholesaler === "string") {
          return <span>{legacyWholesaler}</span>;
        }

        return <span className="text-muted-foreground">—</span>;
      },
    },
    {
      accessorKey: "invoice_number",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Invoice #" />
      ),
      cell: ({ row }) => {
        const inv = row.original.invoice_number;
        if (!inv) return <span className="font-mono text-sm">—</span>;
        return (
          <span className="inline-flex items-center gap-1.5 font-mono text-sm">
            {inv}
            {row.original.has_invoice_document && (
              <Paperclip
                className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
                aria-label="Invoice document attached"
              />
            )}
          </span>
        );
      },
    },
    {
      id: "notes",
      header: () => <div className="text-center">Notes</div>,
      cell: ({ row }) => (
        <NotesCell
          hasNotes={(row.original.notes_count ?? 0) > 0}
          onClickNotes={() => onEditNotes?.(row.original)}
        />
      ),
    },
    {
      id: "actions",
      cell: ({ row }) => (
        <TransactionRowActions
          record={row.original}
          onView={onView}
          onEdit={onEdit}
          onDelete={onDelete}
          onAddToReminder={onAddToReminder}
        />
      ),
    },
  ];
}
